<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headers"
                        :key="idx"
                        scope="col"
                        tabindex="0"
                        :aria-sort="getSortAriaValue(idx)"
                        @click="sortBy(idx, header.type)"
                        @keydown.enter.prevent="sortBy(idx, header.type)"
                        @keydown.space.prevent="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        <span class="inventory-header-label">
                            {{ header.label }}
                            <span v-if="header.hasExplanation" class="shelf-life-info">
                                <button
                                    type="button"
                                    class="shelf-life-info__button"
                                    aria-label="Explain Shelf Life in Days"
                                    aria-describedby="shelf-life-days-explanation"
                                    @click.stop
                                    @keydown.stop
                                >
                                    <font-awesome-icon :icon="['fas', 'circle-info']" />
                                </button>
                                <span
                                    id="shelf-life-days-explanation"
                                    role="tooltip"
                                    class="shelf-life-info__tooltip"
                                >
                                    Remaining shelf life in days.
                                </span>
                            </span>
                        </span>
                    </th>
                    <th v-if="!readOnly" scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, index) in paginatedData"
                    :key="getRowKey(item, index)"
                    :class="rowClass(item)"
                >
                    <td>
                        <PartNumberDisplay
                            :part-number="item.part_number"
                            :selected-part="part_selected"
                        />
                    </td>
                    <td>{{ item.description || '-' }}</td>
                    <td>
                        <span class="inventory-source-label" :class="sourceClass(item.source)">
                            {{ item.source || '-' }}
                        </span>
                    </td>
                    <td>{{ item.warehouse || '-' }}</td>
                    <td>{{ formatNumeric(item.quantity) || '-' }}</td>
                    <td>{{ item.condition || '-' }}</td>
                    <td :class="{ 'text-danger': item.unit_cost === 'NEED PRICING APPROVAL' }">
                        {{ formatInventoryUnitCost(item.unit_cost) }}
                    </td>
                    <td>{{ formatInventoryEntryDate(item.entry_date) }}</td>
                    <td>{{ formatInventoryExpirationDate(item.exp_date) }}</td>
                    <td>{{ formatShelfLifeDays(item.remaining_shelf_life_days) }}</td>
                    <td v-if="!readOnly">
                        <a
                            href="javascript:;"
                            @click="toggleQuoteModal(item)"
                            title="Add Quote"
                            class="dashboard-action-link"
                        >
                            <font-awesome-icon :icon="['fas', 'plus']" />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="inventoryItemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="inventoryItemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import {
        buildDashboardRowHighlightClass,
        compareHighlightedCustomerFirst,
        compareSelectedPartFirst,
        getDashboardRowCustomerName,
    } from '@/helpers/dashboardRowHighlight.js'
    import { formatMoneyValue, formatNumeric, parseNumeric } from '@/helpers/string.js'
    import { formatComponentControlDate } from '@/helpers/dates.js'

    export default {
        name: 'InventoryTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            data: {
                type: Array,
                default: () => [],
            },
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                headers: [
                    { label: 'Part Number', type: 'string' },
                    { label: 'Description', type: 'string' },
                    { label: 'Source', type: 'string' },
                    { label: 'Warehouse', type: 'string' },
                    { label: 'Quantity', type: 'numeric' },
                    { label: 'Cond', type: 'string' },
                    { label: 'Unit Cost', type: 'numeric' },
                    { label: 'Entry Date', type: 'date' },
                    { label: 'Expiration Date', type: 'date', nullsLast: true },
                    {
                        label: 'Remaining Shelf Life',
                        type: 'numeric',
                        nullsLast: true,
                        hasExplanation: true,
                    },
                ],
                sortColumn: 7,
                columnType: 'date',
                sortDirection: 'desc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = a[key]
                    let valueB = b[key]
                    const header = this.headers[this.sortColumn]

                    if (header?.nullsLast) {
                        valueA = this.getNullLastSortValue(valueA, header.type)
                        valueB = this.getNullLastSortValue(valueB, header.type)
                        const valueAIsValid = valueA !== null
                        const valueBIsValid = valueB !== null
                        if (valueAIsValid !== valueBIsValid) {
                            return valueAIsValid ? -1 : 1
                        }
                        if (!valueAIsValid) return 0

                        if (valueA === valueB) return 0
                        return this.sortDirection === 'asc'
                            ? valueA > valueB
                                ? 1
                                : -1
                            : valueA < valueB
                              ? 1
                              : -1
                    }

                    const partPriority = compareSelectedPartFirst(
                        a?.part_number,
                        b?.part_number,
                        this.part_selected
                    )
                    if (partPriority !== 0) return partPriority

                    const customerPriority = compareHighlightedCustomerFirst(
                        getDashboardRowCustomerName(a),
                        getDashboardRowCustomerName(b),
                        this.highlightedCustomer
                    )
                    if (customerPriority !== 0) return customerPriority

                    if (this.columnType === 'numeric') {
                        valueA = parseNumeric(valueA, 0)
                        valueB = parseNumeric(valueB, 0)
                    } else if (this.columnType === 'date') {
                        valueA = formatComponentControlDate(valueA)
                        valueB = formatComponentControlDate(valueB)
                    } else {
                        valueA = String(valueA || '').toLowerCase()
                        valueB = String(valueB || '').toLowerCase()
                    }

                    if (valueA === valueB) return 0
                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.data.length / this.itemsPerPage))
            },
        },
        watch: {
            data: {
                handler() {
                    this.applyDefaultSort()
                },
                immediate: true,
            },
        },
        methods: {
            formatNumeric,
            formatInventoryEntryDate(value) {
                return formatComponentControlDate(value) || '-'
            },
            formatInventoryExpirationDate(value) {
                return this.normalizeExpirationDate(value) || '-'
            },
            formatInventoryUnitCost(value) {
                if (!value) return '-'
                return formatMoneyValue(value) || value
            },
            formatShelfLifeDays(value) {
                if (!this.isNumericValue(value)) return '-'
                return Number(value)
            },
            isNumericValue(value) {
                const numericValue = Number(value)
                return (
                    value !== null &&
                    value !== undefined &&
                    value !== '' &&
                    Number.isInteger(numericValue) &&
                    numericValue >= 0
                )
            },
            normalizeExpirationDate(value) {
                if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value)) {
                    return null
                }

                const formattedValue = formatComponentControlDate(value)
                const match = formattedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
                if (!match) return null

                const year = Number(match[1])
                const month = Number(match[2])
                const day = Number(match[3])
                const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
                const daysInMonth = [
                    31,
                    leapYear ? 29 : 28,
                    31,
                    30,
                    31,
                    30,
                    31,
                    31,
                    30,
                    31,
                    30,
                    31,
                ]
                if (month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) {
                    return null
                }
                return formattedValue
            },
            getNullLastSortValue(value, columnType) {
                if (columnType === 'numeric') {
                    return this.isNumericValue(value) ? Number(value) : null
                }
                if (columnType === 'date') {
                    const expirationDate = this.normalizeExpirationDate(value)
                    return expirationDate ? Number(expirationDate.replace(/-/g, '')) : null
                }
                return null
            },
            getSortAriaValue(columnIndex) {
                if (this.sortColumn !== columnIndex) return 'none'
                return this.sortDirection === 'asc' ? 'ascending' : 'descending'
            },
            applyDefaultSort() {
                this.sortColumn = 7
                this.columnType = 'date'
                this.sortDirection = 'desc'
                this.currentPage = 1
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                const keys = [
                    'part_number',
                    'description',
                    'source',
                    'warehouse',
                    'quantity',
                    'condition',
                    'unit_cost',
                    'entry_date',
                    'exp_date',
                    'remaining_shelf_life_days',
                ]
                return keys[index]
            },
            changePage(page) {
                if (page >= 1 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            toggleQuoteModal(item) {
                this.$emit('toggleQuoteModal', {
                    ...item,
                    part_quoted: item?.part_number || '',
                    description: item?.description || '',
                })
            },
            getRowKey(item, index) {
                return [
                    item.part_number || '',
                    item.source || '',
                    item.warehouse || '',
                    item.condition || '',
                    item.entry_date || '',
                    index,
                ].join(':')
            },
            sourceClass(source) {
                if (source === 'GlobalParts Inventory') {
                    return 'inventory-source-label--global'
                }
                return 'inventory-source-label--greenwood'
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    customerName: getDashboardRowCustomerName(item),
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                })
            },
        },
    }
</script>

<style scoped>
    .inventory-header-label {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
    }

    .shelf-life-info {
        position: relative;
        display: inline-flex;
    }

    .shelf-life-info__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.15rem;
        border: 0;
        border-radius: var(--radius-sm);
        background: transparent;
        color: inherit;
        cursor: help;
        line-height: 1;
    }

    .shelf-life-info__button:focus-visible {
        outline: 2px solid var(--color-brand-primary);
        outline-offset: 2px;
    }

    .shelf-life-info__tooltip {
        position: absolute;
        top: calc(100% + 0.5rem);
        right: 0;
        width: 18rem;
        padding: 0.65rem 0.75rem;
        border: 1px solid var(--color-border-default);
        border-radius: var(--radius-md);
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-lg);
        color: var(--color-text-primary);
        font-size: 0.8rem;
        font-weight: 400;
        line-height: 1.35;
        text-align: left;
        white-space: normal;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.15s ease;
        z-index: 20;
    }

    .shelf-life-info:hover .shelf-life-info__tooltip,
    .shelf-life-info:focus-within .shelf-life-info__tooltip {
        opacity: 1;
        visibility: visible;
    }

    .inventory-source-label {
        display: inline-block;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        font-size: 0.875rem;
        font-weight: 600;
    }

    .inventory-source-label--greenwood {
        color: var(--color-text-secondary);
    }

    .inventory-source-label--global {
        color: var(--color-state-info);
    }
</style>
